import { CanceledError } from 'axios';
import { Formik } from 'formik';
import { useCallback, useContext } from 'react';

import { customersApi } from 'src/app/services/Customers/Customers.service';
import type { ICustomerLeadSalesTaxInfoTabFormValues } from 'src/components/customers/CustomerLeadTypes';
import {
  customerLeadSalesTaxInfoInitialValues,
  customerLeadSalesTaxInfoValidationSchema,
} from 'src/components/customers/steps/Contact/CustomerLeadStepSalesTaxInfoTab.validation';
import { CustomerLeadStepSalesTaxInfoTabContent } from 'src/components/customers/steps/Contact/CustomerLeadStepSalesTaxInfoTabContent';
import { getFilesDiff } from 'src/helpers';
import { CustomersContext } from 'src/pages/CustomersPage/CustomersPage.context';

type CustomerLeadStepSalesTaxInfoTabProps = {
  customerProductLeadId: string;

  onSubmit: () => boolean;
};

export function CustomerLeadStepSalesTaxInfoTab({
  customerProductLeadId,
  onSubmit,
}: Readonly<CustomerLeadStepSalesTaxInfoTabProps>) {
  const { customerLeadProduct, updateCustomerLeadProduct } = useContext(CustomersContext);

  const [, partialUpdateCustomerProductLead, cancelPartialUpdateCustomerProductLead] =
    customersApi.usePartialUpdateCustomerProductLeadMutation(customerProductLeadId);

  const save = useCallback(
    async (newValues: ICustomerLeadSalesTaxInfoTabFormValues, submit: boolean) => {
      cancelPartialUpdateCustomerProductLead();

      try {
        const updatedCustomerProductLeadData = await partialUpdateCustomerProductLead({
          data: {
            salesTaxInfo: {
              salesTaxFiles: getFilesDiff(newValues.salesTaxFiles, customerLeadProduct?.salesTaxFiles ?? []),
              exempt: newValues.exempt,

              submit,
            },
          },
        });

        if (submit) {
          updateCustomerLeadProduct(updatedCustomerProductLeadData.data);
        }
      } catch (unknownError) {
        if (unknownError instanceof CanceledError) {
          console.debug('Condition: auto save request has been cancelled');
        } else {
          console.error(unknownError);
        }
      }
    },
    [
      cancelPartialUpdateCustomerProductLead,
      partialUpdateCustomerProductLead,
      customerLeadProduct?.salesTaxFiles,
      updateCustomerLeadProduct,
    ]
  );

  // Auto save everything except sales tax files
  const handleUpdate = useCallback(
    async (newValues: Omit<ICustomerLeadSalesTaxInfoTabFormValues, 'salesTaxFiles'>) => {
      cancelPartialUpdateCustomerProductLead();

      try {
        const updatedCustomerProductLeadData = await partialUpdateCustomerProductLead({
          data: {
            salesTaxInfo: {
              exempt: newValues.exempt,

              // No changes
              salesTaxFiles: {
                addedFiles: [],
                removedFiles: [],
              },

              submit: false,
            },
          },
        });

        if (customerLeadProduct) {
          updateCustomerLeadProduct({
            ...customerLeadProduct,

            exempt: updatedCustomerProductLeadData.data.exempt,
          });
        }
      } catch (unknownError) {
        if (unknownError instanceof CanceledError) {
          console.debug('Condition: auto save request has been cancelled');
        } else {
          console.error(unknownError);
        }
      }
    },
    [
      cancelPartialUpdateCustomerProductLead,
      partialUpdateCustomerProductLead,
      customerLeadProduct,
      updateCustomerLeadProduct,
    ]
  );

  const handleSubmit = useCallback(
    async (submitValues: ICustomerLeadSalesTaxInfoTabFormValues) => {
      await save(submitValues, true);

      return onSubmit();
    },
    [save, onSubmit]
  );

  if (!customerLeadProduct) {
    return null;
  }

  return (
    <Formik<ICustomerLeadSalesTaxInfoTabFormValues>
      initialValues={customerLeadSalesTaxInfoInitialValues(customerLeadProduct)}
      validationSchema={customerLeadSalesTaxInfoValidationSchema}
      validateOnMount={true}
      enableReinitialize={true}
      onSubmit={handleSubmit}
    >
      {(formikProps) => <CustomerLeadStepSalesTaxInfoTabContent formikProps={formikProps} onUpdate={handleUpdate} />}
    </Formik>
  );
}
